import { Fragment } from "react";
import { makeStyles } from "tss-react/mui";
import { MiChip } from "@miplatform/components";
import { formatDate, isRecentlyAdded } from "@miplatform/utils";
import { Box, Divider, Typography } from "@mui/material";

export function SectionInfo({
  data,
  chip,
  disableNewChip = false,
}: ISectionInfoCardProps) {
  const { classes, theme } = useStyles();

  const isNew = isRecentlyAdded(data.createdOn);

  return (
    <Box className={classes.root}>
      <Box className={classes.configListCardText}>
        <Box className={classes.configListCardTextLine}>
          <Typography variant="HD2" color={theme.palette.primary.main}>
            {data.name}
          </Typography>
          {data.additionalTitleText?.map((text, index) => (
            <Fragment key={`${text}-${index}`}>
              <Divider orientation="vertical" />
              <Typography variant="BD2" color={theme.palette.text.secondary}>
                {text}
              </Typography>
            </Fragment>
          ))}
        </Box>
        {data.subtitle ? (
          <Box className={classes.configListCardTextLine} mb={0.5}>
            <Typography variant="BD2" color={theme.palette.text.secondary}>
              {data.subtitle}
            </Typography>
          </Box>
        ) : null}
        <Box className={classes.configListCardTextLine}>
          {chip}
          {isNew && !disableNewChip ? (
            <MiChip label="New" className={classes.newBadge} />
          ) : null}
          {data.modifiedOn && (
            <>
              <Typography variant="HD1">Last updated: </Typography>
              <Typography variant="HD1" fontWeight={400}>
                {formatDate(data.modifiedOn, "MM/DD/YYYY")}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  configListCardContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  configListCardText: {
    display: "flex",
    flexDirection: "column",
  },
  configListCardTextLine: {
    height: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  newBadge: {
    backgroundColor: theme.palette.warning.light,
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.warning.main,
    borderRadius: 2,
    height: 18,
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.small,
    "& .MuiChip-label": {
      padding: theme.spacing(1),
    },
  },
}));

interface ISectionInfoCardProps {
  data: {
    name: string;
    createdOn?: string;
    modifiedOn?: string;
    additionalTitleText?: string[];
    subtitle?: string;
  };
  disableNewChip?: boolean;
  chip?: React.ReactNode;
}
